import { useSearchQuestionQSetUsageRecordsQuery } from '@report/graphql';
import { OnRowOverlayItemAnalysisTable } from '@report/qbank-item-analysis';
import {
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  type TableContainerProps,
} from '@shared/components';
import { TableHeader } from '@shared/table-generator';

import { useQuestionUsageByQSetReportSearchParams } from '../../hooks';
import { QuestionUsageByQSetReportTableBody } from '../question-usage-by-qset-report-table-body';

import { questionUsageByQSetReportTableHeader } from './question-usage-by-qset-report-table-header';

interface QuestionUsageByQSetReportTableProps extends TableContainerProps {
  examCoursesOptions: { label: string; value: string }[];
}
export default function QuestionUsageByQSetReportTable(
  props: Readonly<QuestionUsageByQSetReportTableProps>,
) {
  const { examCoursesOptions, ...restProps } = props;
  const [{ page, pageSize, examCourse, qSetStatus }, { changePage, changePageSize }] =
    useQuestionUsageByQSetReportSearchParams();

  const { data: questionUsageData, loading: questionUsageLoading } =
    useSearchQuestionQSetUsageRecordsQuery({
      variables: {
        searchQuestionQSetUsageRecordsInput: {
          qSetStatuses: qSetStatus ? [qSetStatus] : [],
          examCourseId: examCourse,
          paging: {
            pageSize: pageSize,
            currentPage: page,
          },
        },
      },
      skip: !examCourse || qSetStatus === null,
    });

  const examCourseName =
    examCoursesOptions.find((option) => option.value === examCourse)?.label ?? '';

  const questionUsageDataRecords =
    questionUsageData?.reportSearchQuestionQSetUsageRecords.records || [];
  const total = questionUsageData?.reportSearchQuestionQSetUsageRecords.total || 0;
  const availableQSetCodeNumbers =
    questionUsageDataRecords[0]?.qSetUsages.map((usage) => usage.qSetCodeNumber) || [];

  return (
    <TableContainer
      {...restProps}
      sx={{
        borderRadius: (theme) => theme.shape.rounded(2),
        overflowX: 'auto',
        border: '1px solid',
        borderColor: (theme) => theme.palette.grey[300],
        ...restProps.sx,
      }}
    >
      <Table
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <TableHeader
          columns={questionUsageByQSetReportTableHeader(availableQSetCodeNumbers, examCourseName)}
          slotProps={{
            tableHeader: {
              sx: (theme) => ({
                bgcolor: theme.palette.common.black,
                borderColor: theme.palette.grey[300],
                border: 'none',
              }),
            },
            tableHeaderRow: {
              sx: {
                ':first-of-type': {
                  'th:last-of-type': {
                    borderRight: 'none',
                  },
                },
                ':last-of-type > th': {
                  borderTop: '1px solid',
                  borderColor: (theme) => theme.palette.grey[300],
                },
              },
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.common.black,
                padding: theme.spacing(3),
                borderRight: '1px solid',
                borderBottom: 'none',
                borderColor: theme.palette.grey[300],
              }),
            },
          }}
        />
        {total === 0 ? null : (
          <>
            <TableBody>
              <QuestionUsageByQSetReportTableBody rows={questionUsageDataRecords} />
            </TableBody>
            <TablePagination
              count={total}
              page={page}
              onPageChange={(event, newPage) => changePage(newPage)}
              rowsPerPageOptions={[50, 100, 150, 200]}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => changePageSize(Number(event.target.value))}
            />
          </>
        )}
      </Table>
      <OnRowOverlayItemAnalysisTable
        total={total}
        isLoading={questionUsageLoading}
        isFilterSet={!!examCourse || qSetStatus !== null}
      />
    </TableContainer>
  );
}
